import React from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Import styles
import './InvoiceDetailsPopup.css';

// Import hooks
import { useServiceCharges } from './Hooks/InvoiceDetailsPopup.hooks';

// Import PDF generator
import { generateInvoicePDFDirect } from '../../../utils/pdfGenerator';

// Import subcomponents
import {
    InvoiceActionButtons,
    InvoiceBrandingSection,
    BillFromToSection,
    PeriodCardsSection,
    ServiceChargesTable,
    InvoiceSummarySection,
    InvoiceFooter,
    ServiceChargesLoadingSpinner,
} from './Subcomponents/InvoiceDetailsPopup.subcomponents';

// Import utilities
import {
    transformAPIDataToServiceCharges,
    calculateInvoiceSummary,
    parseCompanyAndSeller,
    getEarliestCreatedDate,
    calculatePeriodToDate,
    getWarehouseNames,
    isInvoicePaid,
    getCategoryTotals,
} from './utils/InvoiceDetailsPopup.utils';

// Main Component: Invoice Details Popup
const InvoiceDetailsPopup = props => {
    const { showPopup, hidePopup, invoiceRecord, onResendEmail, onWhatsApp } = props;

    // Fetch service charges when popup opens
    const { isLoading: isLoadingServiceCharges, apiData: serviceChargesApiData } = useServiceCharges(
        invoiceRecord,
        showPopup
    );

    // Parse company and seller from combined companyName string
    const { company: displayCompany, seller: displaySeller } = parseCompanyAndSeller(invoiceRecord.companyName);

    // Extract hasRentItems for cycle determination
    const hasRentItems = invoiceRecord.hasRentItems || false;

    // Transform API data if available
    const serviceCharges = transformAPIDataToServiceCharges(serviceChargesApiData);
    const warehouseName = getWarehouseNames(serviceCharges);
    const isPaid = isInvoicePaid(invoiceRecord.status);
    const categoryTotals = getCategoryTotals(serviceCharges);

    // Stored invoice period; older responses without it fall back to the earliest charge date
    const periodFromDate = invoiceRecord.fromDate || getEarliestCreatedDate(serviceChargesApiData);
    const periodToDate = invoiceRecord.toDate || calculatePeriodToDate(periodFromDate, hasRentItems);

    // Calculate summary from serviceCharges
    const taxRate = 0;
    const { subtotal, totalAdditionalCharges, tax, totalDue } = calculateInvoiceSummary(serviceCharges, taxRate);

    // Handler: Generate PDF using jsPDF Direct
    const handleDownloadPDFDirect = async () => {
        try {
            await generateInvoicePDFDirect(
                { ...invoiceRecord, warehouseName },
                serviceCharges,
                { subtotal, totalAdditionalCharges, tax, totalDue, categoryTotals, paymentMethod: isPaid ? 'Paid From Wallet' : undefined },
                {
                    displayCompany,
                    displaySeller,
                    periodFromDate,
                    periodToDate,
                    hasRentItems,
                }
            );
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Failed to download invoice PDF. Please try again.');
        }
    };

    return (
        <Modal
            {...props}
            show={showPopup}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            onHide={() => hidePopup(!showPopup)}
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton>
                <div style={{ width: '100%' }}>
                    <Modal.Title id="contained-modal-title-vcenter" style={{ fontSize: '18px', fontWeight: '600' }}>
                        Invoice {invoiceRecord.invoiceNumber}
                    </Modal.Title>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                        {displayCompany}
                        {displaySeller ? ` — ${displaySeller}` : ''}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body style={{ padding: '30px' }}>
                {/* Header with Actions - NOT included in PDF */}
                <div
                    data-testid="invoice-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                    }}
                >
                    <div />
                    <div data-testid="invoice-action-buttons">
                        <InvoiceActionButtons
                            invoiceRecord={invoiceRecord}
                            onDownloadPDF={handleDownloadPDFDirect}
                            onResendEmail={onResendEmail}
                            onWhatsApp={onWhatsApp}
                            isLoading={isLoadingServiceCharges}
                        />
                    </div>
                </div>

                {/* Invoice PDF Content - Everything below goes to PDF */}
                <div id="invoice-pdf-content" style={{ padding: '0', backgroundColor: '#fff' }}>
                    {/* Branding Section */}
                    <div data-testid="invoice-branding-section" className="invoice-branding-section pdf-page-break-avoid">
                        <InvoiceBrandingSection invoiceRecord={invoiceRecord} />
                    </div>

                    {/* Bill From / Bill To */}
                    <div data-testid="bill-from-to-section" className="bill-from-to-section pdf-page-break-avoid">
                        <BillFromToSection
                            displayCompany={displayCompany}
                            displaySeller={displaySeller}
                            invoiceRecord={invoiceRecord}
                            warehouseName={warehouseName}
                        />
                    </div>

                    {/* Period Cards */}
                    <div data-testid="period-cards-section" className="period-cards-section pdf-page-break-avoid">
                        <PeriodCardsSection
                            invoiceRecord={invoiceRecord}
                            hasRentItems={hasRentItems}
                            periodFromDate={periodFromDate}
                            periodToDate={periodToDate}
                        />
                    </div>

                    {/* Service Charges Table */}
                    <div data-testid="service-charges-section" className="service-charges-section">
                        {isLoadingServiceCharges ? (
                            <ServiceChargesLoadingSpinner />
                        ) : (
                            <ServiceChargesTable serviceCharges={serviceCharges} />
                        )}
                    </div>

                    {/* Summary Section */}
                    <div data-testid="invoice-summary-section" className="invoice-summary-section pdf-page-break-avoid">
                        <InvoiceSummarySection
                            subtotal={subtotal}
                            additionalCharges={totalAdditionalCharges}
                            taxRate={taxRate}
                            totalDue={totalDue}
                            isPaid={isPaid}
                            categoryTotals={categoryTotals}
                        />
                    </div>

                    {/* Footer */}
                    <div className="invoice-footer-section">
                        <InvoiceFooter />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

InvoiceDetailsPopup.propTypes = {
    showPopup: PropTypes.bool.isRequired,
    hidePopup: PropTypes.func.isRequired,
    invoiceRecord: PropTypes.object.isRequired,
    onDownloadPDF: PropTypes.func,
    onResendEmail: PropTypes.func,
    onWhatsApp: PropTypes.func,
};

export default InvoiceDetailsPopup;
